import { useTranslation } from 'react-i18next';

import WarningIcon from '@/assets/icons/warning.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { SmoltReportWarnings } from '@/domain/smoltReport/types';
import { formatDateShortLocalized } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import { computeSmoltReportWarnings } from '../utils/computeWarnings';

import styles from './SmoltReportWarningsList.module.scss';

interface SmoltReportWarningsListProps {
    warnings: SmoltReportWarnings;
    deliveryDate: string;
    /** `YYYY-MM-DD`; null on reports stored before it was recorded. */
    lastMeasurementDay: string | null;
    /** `YYYY-MM-DD`; null on reports stored before it was recorded. */
    welfareWindowEnd: string | null;
}

export default function SmoltReportWarningsList(props: SmoltReportWarningsListProps) {
    const { warnings, deliveryDate, lastMeasurementDay, welfareWindowEnd } = props;

    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);
    const format = (day: string | null) =>
        day != null ? formatDateShortLocalized(day, locale) : undefined;
    const items = computeSmoltReportWarnings(warnings, t, {
        deliveryDate: formatDateShortLocalized(deliveryDate, locale),
        lastMeasurementDay: format(lastMeasurementDay),
        welfareWindowEnd: format(welfareWindowEnd),
    });

    if (items.length === 0) {
        return null;
    }

    return (
        <div className={styles.warningsContainer}>
            {items.map(({ key, message }) => (
                <Alert
                    key={key}
                    tone="warning"
                    size="small"
                    live="off"
                    icon={
                        <SvgIcon
                            icon={WarningIcon}
                            size="custom"
                            customSize={20}
                            color="currentColor"
                        />
                    }>
                    {message}
                </Alert>
            ))}
        </div>
    );
}
